import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

interface AiAssistantContextType {
  isOpen: boolean
  stageId: number
  subStageId: string
  fieldName: string

  openAssistant: () => void
  closeAssistant: () => void
  toggleAssistant: () => void

  setStage: (id: number) => void
  setSubStage: (id: string) => void
  setFieldName: (name: string) => void

  reset: () => void
}

type Props = {
  children: React.ReactNode
}

const AiAssistantContext = createContext<
  AiAssistantContextType | undefined
>(undefined)

export const AiAssistantProvider = ({ children }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [stageId, setStageId] = useState(1)
  const [subStageId, setSubStageId] = useState('')
  const [fieldName, setFieldNameState] = useState('')

  const openAssistant = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeAssistant = useCallback(() => {
    setIsOpen(false)
  }, [])

  const toggleAssistant = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [])

  const setStage = useCallback((id: number) => {
    setStageId(id)

    // reset dependent state
    setSubStageId('')
    setFieldNameState('')
  }, [])

  const setSubStage = useCallback((id: string) => {
    setSubStageId(id)

    // reset dependent state
    setFieldNameState('')
  }, [])

  const setFieldName = useCallback((name: string) => {
    setFieldNameState(name)
  }, [])

  const reset = useCallback(() => {
    setIsOpen(false)
    setStageId(1)
    setSubStageId('')
    setFieldNameState('')
  }, [])

  const value = useMemo(
    () => ({
      isOpen,
      stageId,
      subStageId,
      fieldName,

      openAssistant,
      closeAssistant,
      toggleAssistant,

      setStage,
      setSubStage,
      setFieldName,

      reset,
    }),
    [
      isOpen,
      stageId,
      subStageId,
      fieldName,
      openAssistant,
      closeAssistant,
      toggleAssistant,
      setStage,
      setSubStage,
      setFieldName,
      reset,
    ]
  )

  return (
    <AiAssistantContext.Provider value={value}>
      {children}
    </AiAssistantContext.Provider>
  )
}

export const useAiAssistant = () => {
  const context = useContext(AiAssistantContext)

  if (!context) {
    throw new Error(
      'useAiAssistant must be used within AiAssistantProvider'
    )
  }

  return context
}